import React, { useState, useRef, useEffect } from "react";
import { useField } from "formik";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { Box, IconButton, Tooltip, CircularProgress, Menu, MenuItem } from "@mui/material";
import MicNoneIcon from '@mui/icons-material/MicNone';
import SendIcon from "@mui/icons-material/Send";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { primaryColor } from "../../Theme";
import {
  RecordingIndicator,
  ConnectionIndicator,
  VoiceSearchInputContainer,
  VoiceSearchPill,
  MicButtonWrapper,
  IconButtonWrapper,
  VoiceWaveContainer,
  VoiceWaveBar,
  RippleSpan,
} from "./styles";
import {
  MAX_UPDATE_RATE,
  MAX_TEXT_LENGTH,
  VOICE_API_URL,
  VOICE_WS_URL,
  getWebSocketUrl,
  getWebSocketProtocols,
  getAudioWorkletModuleUrl,
  createProcessingPayload,
} from "../../utils/AIPrescription/voiceInputUtils";
import { devError, devLog } from "../../utils/safeClientLog";
import {
  isVttFileName,
  isValidWebVttHeader,
  MAX_VTT_FILE_BYTES,
} from "../../utils/AIPrescription/vttFileUtils";
import { handleExpiredAuthSession } from "../../utils/authCookie";

interface VoiceSearchInputProps {
  name: string;
  id?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  type?: string;
  multiline?: boolean;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  onVoiceResult?: (text: string) => void;
  onSearch?: (searchText: string, options?: { vttContent?: string }) => void;
  onVttContentChange?: (content: string | null) => void;
  /** Current .vtt body from parent (for search icon submit) */
  pendingVttContent?: string | null;
  onProcessResponse?: (response: any) => void;
  onProcessRequestReady?: (sendRequest: (text: string, metadata?: any) => void) => void;
  voiceSearchCountdown?: number | null;
  /** Agentic/Voice: mic controls. Transcript: VTT upload. Default agentic. */
  prescribeMode?: "agentic" | "transcribe";
  /** When set, shows Voice / Transcript dropdown inside the search pill. */
  onPrescribeModeChange?: (mode: "agentic" | "transcribe") => void;
  [key: string]: any;
}

const PRESCRIBE_MODE_OPTIONS = [
  {
    id: "agentic" as const,
    label: "Voice",
    hint: "Speak to prescribe",
    Icon: MicNoneIcon,
  },
  {
    id: "transcribe" as const,
    label: "Transcript",
    hint: "Upload a .vtt file",
    Icon: DescriptionOutlinedIcon,
  },
] as const;

// Container styling for a button using the click ripple below — lifts the
// icon onto its own stacking layer (z-index 1) so the ripple (z-index 0)
// stays behind it instead of painting over the glyph.
const rippleContainerSx = {
  position: "relative" as const,
  overflow: "hidden" as const,
  "& .MuiSvgIcon-root, & .MuiCircularProgress-root": {
    position: "relative" as const,
    zIndex: 1,
  },
};

export default function VoiceSearchInput(props: VoiceSearchInputProps) {
  const {
    onVoiceResult,
    onSearch,
    onVttContentChange,
    pendingVttContent,
    onProcessResponse,
    onProcessRequestReady,
    disabled,
    id,
    label,
    placeholder,
    className,
    type,
    multiline,
    rows,
    minRows,
    maxRows,
    onKeyDown,
    voiceSearchCountdown,
    prescribeMode = "agentic",
    onPrescribeModeChange,
    onChange: propsOnChange,
    ...rest
  } = props;
  const [field, meta] = useField(props);
  const [isRecording, setIsRecording] = useState(false);
  // How many wave bars fit the current width of the text/wave slot, at the
  // fixed bar width + gap below — recomputed on resize so the wave always
  // fills the slot instead of a hardcoded bar count.
  const [waveBarCount, setWaveBarCount] = useState(9);
  // One counter per icon button, incremented on every click. Each button's
  // RippleSpan is keyed off its counter, so a new click always remounts a
  // fresh element and replays the full ripple animation from the start —
  // independent of how long the previous click's animation had left to run.
  const [rippleTokens, setRippleTokens] = useState<Record<string, number>>({});
  const triggerRipple = (id: string) => {
    setRippleTokens((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1 }));
  };
  // Clear/Upload/Send/Done mount and unmount as isRecording toggles (each is
  // only rendered on one side of that condition). Without this, a button that
  // still held a truthy ripple token from an earlier click would replay its
  // ripple purely from being remounted — with no new click involved — the
  // moment it reappeared. Called in the same state update as setIsRecording
  // so the reset lands in the same render as the mount/unmount, not a
  // following one (which would let the stray ripple flash first).
  const resetTransientRippleTokens = () => {
    setRippleTokens((prev) => ({ ...prev, upload: 0, send: 0, done: 0 }));
  };
  const [isConnected, setIsConnected] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Local state for instant display (bypasses Formik render delay)
  const [displayValue, setDisplayValue] = useState(field.value || "");

  // Sync displayValue with Formik when it changes externally (e.g., form reset)
  // Skip sync if we're updating from voice input (to prevent interference)
  // CRITICAL: Never overwrite voice text during active recording
  useEffect(() => {
    // Sync Formik -> local display when Formik changes externally (e.g. suggestion/pharmacy chip).
    // Do not overwrite when local display is ahead of Formik (typing/voice batching: display extends field.value).
    if (isRecording || isVoiceUpdateRef.current) return;

    const fv = (field.value ?? "") as string;
    const dv = (displayValue ?? "") as string;
    if (fv === dv) return;

    // Only treat local display as "ahead" (typing/voice batching) when Formik still holds
    // a non-empty prefix of it. Without the fv.length check, a clear-to-"" (e.g. chip auto-send's
    // formikRef.setFieldValue(..., "")) is falsely seen as "behind" — every string "starts with"
    // "" — so the box never visually clears even though Formik's value did.
    const formikBehind =
      typeof fv === "string" &&
      typeof dv === "string" &&
      fv.length > 0 &&
      dv.startsWith(fv) &&
      dv.length > fv.length;

    if (formikBehind) return;

    setDisplayValue(fv);
    accumulatedTextRef.current = fv;
  }, [field.value, isRecording, displayValue]); // Include isRecording to prevent overwrites during recording

  // Auto-scroll to end when displayValue changes (for multiline textareas)
  useEffect(() => {
    if (inputRef.current && multiline && displayValue) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (inputRef.current) {
          const textarea = inputRef.current as HTMLTextAreaElement;
          textarea.scrollTop = textarea.scrollHeight;
        }
      }, 0);
    }
  }, [displayValue, multiline]);

  // Recompute how many wave bars fit the slot's current width (at the fixed
  // bar width + gap) whenever it resizes, so the wave fills whatever width
  // this input renders at instead of a hardcoded bar count.
  useEffect(() => {
    const slotEl = waveSlotRef.current;
    if (!slotEl || typeof ResizeObserver === "undefined") return;

    const updateBarCount = () => {
      const width = slotEl.getBoundingClientRect().width;
      const count = Math.max(
        1,
        Math.floor((width + WAVE_BAR_GAP) / (WAVE_BAR_WIDTH + WAVE_BAR_GAP))
      );
      waveBarRefs.current.length = count;
      setWaveBarCount(count);
    };

    updateBarCount();
    const resizeObserver = new ResizeObserver(updateBarCount);
    resizeObserver.observe(slotEl);
    return () => resizeObserver.disconnect();
  }, []);

  // WebSocket and audio refs
  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioTrackRef = useRef<MediaStreamTrack | null>(null);
  const silenceWatchdogRef = useRef<NodeJS.Timeout | null>(null);
  const pcmFramesReceivedRef = useRef<number>(0);
  // Full combined text (pre-existing typed/voice text + this recording session's voice
  // text) — used by everything outside the WS message handler (search, send, clear, etc).
  const accumulatedTextRef = useRef<string>("");
  // Text that existed in the field BEFORE the current recording session started. Captured
  // once in startRecording so speaking into a field that already has text APPENDS instead
  // of replacing it.
  const sessionPrefixRef = useRef<string>("");
  // This recording session's permanently-committed (finalized) voice transcript —
  // excludes sessionPrefixRef and excludes the current in-progress partial guess.
  const committedVoiceTextRef = useRef<string>("");
  // The current in-progress partial/interim guess for the segment being spoken right
  // now. Replaced wholesale on each partial message (AWS revises it in place) and
  // cleared once the matching final arrives.
  const interimVoiceTextRef = useRef<string>("");
  // After Send/arrow submit, ignore late WS transcript segments so they can't
  // refill the input we just cleared.
  const ignoreTranscriptRef = useRef<boolean>(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const vttFileInputRef = useRef<HTMLInputElement | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const maxReconnectAttempts = 5;
  const reconnectDelayRef = useRef<number>(1000); // Start with 1 second, will use exponential backoff
  // Mirrors websocketManager.ts: once the fast reconnect burst is exhausted, keep a slow
  // background retry instead of giving up forever, so voice dictation self-heals after a
  // backend restart without requiring a page refresh.
  const slowReconnectDelayMs = 30000;
  const isManualCloseRef = useRef<boolean>(false);
  // True once the user has pressed the mic at least once this page visit (never reset —
  // deliberately sticky for the component's lifetime, not per-connection, so an
  // auto-reconnect triggered by startRecording itself can't race a per-open reset back
  // to false). The websocket connection opens eagerly on mount (see the mount-time
  // useEffect below) and stays warm for the whole page session, independent of whether
  // voice is ever used — but the backend's no-first-audio timeout (live_audio_transcribe.py
  // FIRST_AUDIO_TIMEOUT_SECONDS) starts counting from the moment it accepts the connection,
  // not from when recording actually begins. Without this guard, anyone who never touches
  // the mic would still get a "No audio received" error ~30s after the page loads.
  const recordingAttemptedRef = useRef<boolean>(false);
  // Tracks whether we've told the backend to open an AWS Transcribe stream for the
  // CURRENT attempt (see live_audio_transcribe.py's start_recording/stop_recording
  // protocol) — distinct from isRecording, which only reflects local UI/audio-graph
  // state. Lets stopRecording (and the startRecording catch block, if setup fails
  // after the message was already sent) know whether there's a server-side session
  // to close, so we never leave an AWS stream open with nothing left to feed it.
  const recordingSessionActiveRef = useRef<boolean>(false);
  const isVoiceUpdateRef = useRef<boolean>(false); // Flag to track voice updates
  const updateQueueRef = useRef<string | null>(null); // Queue for batched updates
  const pendingUpdateRef = useRef<boolean>(false); // Flag to prevent duplicate updates
  const lastUpdateTimeRef = useRef<number>(0); // Track last update time for throttling

  // Soundwave visualizer: a read-only analyser tap off the SAME source node
  // used for real transcription (sourceRef) — connected in parallel with the
  // existing worklet, so it never touches what gets recorded or sent.
  const waveAnalyserRef = useRef<AnalyserNode | null>(null);
  // Smoothed amplitude carried across frames so the bars ease toward the new
  // level instead of snapping straight to each raw analyser reading.
  const waveSmoothedAmplitudeRef = useRef<number>(0);
  const waveFrequencyDataRef = useRef<Uint8Array | null>(null);
  const waveRafIdRef = useRef<number | null>(null);
  const waveBarRefs = useRef<(HTMLDivElement | null)[]>([]);
  // The text/wave slot Box — measured to size how many bars fit at the fixed
  // spacing below, so the wave always fills whatever width this input renders
  // at (e.g. wider in a multiline prescription textarea) rather than a fixed count.
  const waveSlotRef = useRef<HTMLDivElement | null>(null);
  const WAVE_BAR_WIDTH = 3;
  const WAVE_BAR_GAP = 3; // must match VoiceWaveContainer's `gap` in styles.ts
  const WAVE_IDLE_SCALE = 0.2;
  // Per-bar smoothed levels so each bar eases independently (real waveform feel).
  const waveBarLevelsRef = useRef<number[]>([]);

  // Shade of the app's own primaryColor (same blue used by the mic/send/upload
  // icons) instead of an unrelated rainbow palette — center bar full strength,
  // fading toward the edges. Works for any bar count so it doesn't need to be
  // re-listed as the count changes with the slot's width.
  const getWaveBarColor = (index: number, total: number) => {
    const center = (total - 1) / 2;
    const normalizedDistance = center > 0 ? Math.abs(index - center) / center : 0;
    const alpha = Math.round(255 * (1 - normalizedDistance * 0.75));
    const hexAlpha = Math.max(0, Math.min(255, alpha)).toString(16).padStart(2, "0");
    return `${primaryColor}${hexAlpha}`;
  };

  const isDisabled = Boolean(disabled);
  const isAgenticMode = prescribeMode !== "transcribe";
  const isTranscribeMode = prescribeMode === "transcribe";
  const hasSearchValue = Boolean(
    (displayValue ?? "").trim() || pendingVttContent?.trim()
  );
  const showSendArrow = hasSearchValue || isRecording;
  const [modeMenuAnchor, setModeMenuAnchor] = useState<null | HTMLElement>(null);
  const modeMenuOpen = Boolean(modeMenuAnchor);
  const modeLabel = isTranscribeMode ? "Transcript" : "Voice";
  const ModeTriggerIcon = isTranscribeMode ? DescriptionOutlinedIcon : MicNoneIcon;
  // Tracks the in-flight/completed AudioWorklet registration for the current
  // audioContextRef instance. A fresh AudioContext needs the module registered again,
  // so this is reset to null whenever a new context is created.
  const workletLoadPromiseRef = useRef<Promise<void> | null>(null);

  // Creates the AudioContext and registers the AudioWorklet processor if that hasn't
  // happened yet for the current context, otherwise returns the already-settled work.
  // Called both eagerly on mount (below) and from startRecording — pulling the network
  // fetch + processor registration out of the mic-click path is what removes the
  // "mic opening" lag: previously this ran fresh on every first click of the page
  // session, sitting between the click and audio actually flowing.
  const ensureAudioPipelineReady = (): Promise<AudioContext> => {
    let audioContext = audioContextRef.current;
    if (!audioContext || audioContext.state === "closed") {
      audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)({
          sampleRate: 16000,
        });
      audioContextRef.current = audioContext;
      workletLoadPromiseRef.current = null;
    }

    if (!workletLoadPromiseRef.current) {
      const workletModuleUrl = getAudioWorkletModuleUrl();
      workletLoadPromiseRef.current = audioContext.audioWorklet
        .addModule(workletModuleUrl)
        .catch((moduleError: any) => {
          if (moduleError?.message?.includes("already been added")) {
            return;
          }
          // Let a future call retry instead of caching a permanent failure.
          workletLoadPromiseRef.current = null;
          throw moduleError;
        });
    }

    const readyContext = audioContext;
    const pendingLoad = workletLoadPromiseRef.current as Promise<void>;
    return pendingLoad.then(() => readyContext as AudioContext);
  };

  // Resets the 5 bars to their resting scale (idle look, no animation frame running).
  const resetWaveBars = () => {
    waveBarRefs.current.forEach((bar) => {
      if (bar) bar.style.transform = `scaleY(${WAVE_IDLE_SCALE})`;
    });
  };

  // One frame of the soundwave: reads live frequency bins and drives each bar
  // independently (smoothed + soft idle breath) for a denser, more organic ripple.
  const renderWaveFrame = () => {
    const analyser = waveAnalyserRef.current;
    const frequencyData = waveFrequencyDataRef.current;
    if (!analyser || !frequencyData) return;

    analyser.getByteFrequencyData(frequencyData);

    const barCount = waveBarRefs.current.length;
    if (waveBarLevelsRef.current.length !== barCount) {
      waveBarLevelsRef.current = new Array(barCount).fill(0);
    }

    const now = performance.now();

    waveBarRefs.current.forEach((bar, index) => {
      if (!bar) return;
      // Map bar index across the usable mid-frequency range for a fuller look.
      const bin = Math.min(
        frequencyData.length - 1,
        Math.floor((index / Math.max(barCount - 1, 1)) * (frequencyData.length * 0.65))
      );
      const level = frequencyData[bin] / 255;
      const prev = waveBarLevelsRef.current[index] ?? 0;
      const smoothed = prev + (level - prev) * 0.35;
      waveBarLevelsRef.current[index] = smoothed;

      // Soft idle breath so quiet moments still feel alive.
      const breath = 0.12 + Math.sin(now / 380 + index * 0.45) * 0.06;
      const scale = Math.min(
        Math.max(WAVE_IDLE_SCALE + smoothed * 1.9, breath),
        2.2
      );
      bar.style.transform = `scaleY(${scale.toFixed(3)})`;
    });

    waveRafIdRef.current = requestAnimationFrame(renderWaveFrame);
  };

  // Taps the already-open recording source with a parallel AnalyserNode —
  // additive only, doesn't touch the existing source -> worklet -> destination
  // chain that feeds real transcription.
  const startWaveVisualizer = (audioContext: AudioContext, source: MediaStreamAudioSourceNode) => {
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.65;
    source.connect(analyser);
    waveAnalyserRef.current = analyser;
    waveFrequencyDataRef.current = new Uint8Array(analyser.frequencyBinCount);
    waveSmoothedAmplitudeRef.current = 0;
    waveBarLevelsRef.current = [];
    if (waveRafIdRef.current === null) {
      waveRafIdRef.current = requestAnimationFrame(renderWaveFrame);
    }
  };

  const stopWaveVisualizer = () => {
    if (waveRafIdRef.current !== null) {
      cancelAnimationFrame(waveRafIdRef.current);
      waveRafIdRef.current = null;
    }
    if (waveAnalyserRef.current) {
      try {
        waveAnalyserRef.current.disconnect();
      } catch (e) {
        console.log("Error disconnecting wave analyser:", e);
      }
      waveAnalyserRef.current = null;
    }
    waveFrequencyDataRef.current = null;
    waveSmoothedAmplitudeRef.current = 0;
    waveBarLevelsRef.current = [];
    resetWaveBars();
  };

  // Connect WebSocket when component mounts (on ai-prescription route)
  useEffect(() => {
    connectWebSocket();
    // Prewarm in parallel with the WebSocket connect — doesn't need mic permission,
    // just the context + worklet module, so it's safe to do before any user gesture.
    ensureAudioPipelineReady().catch((err) => {
      devLog("Audio pipeline prewarm failed; will retry on mic click", err);
    });

    // Cleanup on unmount (disconnect when navigating away)
    return () => {
      isManualCloseRef.current = true; // Prevent reconnection on unmount
      stopRecording();
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      // Clean up update queue and timers
      if (updateQueueRef.current) {
        // Flush any pending updates before unmount
        try {
          setDisplayValue(updateQueueRef.current);
          field.onChange({
            target: {
              name: field.name,
              value: updateQueueRef.current,
            },
          });
        } catch (error) {
          console.error("Error flushing pending update on unmount:", error);
        }
        updateQueueRef.current = null;
      }
      pendingUpdateRef.current = false;
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (e) {
          console.log("Error closing WebSocket on unmount:", e);
        }
        wsRef.current = null;
      }
    };
  }, []);

  // Exponential backoff reconnection strategy
  const attemptReconnect = () => {
    // Don't reconnect if manually closed or max attempts reached
    if (isManualCloseRef.current) {
      return;
    }

    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      // Don't permanently give up — the backend may be restarting or the auth session may
      // still be valid but momentarily unreachable. Fall back to a slow background retry
      // (same pattern as websocketManager.ts) so the mic recovers on its own; the user can
      // still hit Retry immediately via handleRetryConnection.
      console.warn(
        `Voice WebSocket reconnect cap (${maxReconnectAttempts}) hit; scheduling slow background retry in ${slowReconnectDelayMs}ms.`
      );
      setError("Connection lost. Click retry, or it will retry automatically.");
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      reconnectTimerRef.current = setTimeout(() => {
        if (isManualCloseRef.current) {
          return;
        }
        reconnectAttemptsRef.current = 0;
        reconnectDelayRef.current = 1000;
        if (wsRef.current) {
          try {
            wsRef.current.close();
          } catch (e) {
            console.log("Error closing WebSocket before slow retry:", e);
          }
          wsRef.current = null;
        }
        connectWebSocket();
      }, slowReconnectDelayMs);
      return;
    }

    reconnectAttemptsRef.current += 1;
    const delay = reconnectDelayRef.current;

    console.log(`Attempting to reconnect (${reconnectAttemptsRef.current}/${maxReconnectAttempts}) in ${delay}ms...`);

    reconnectTimerRef.current = setTimeout(() => {
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s
      reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, 16000);

      // Close existing connection if it exists
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (e) {
          console.log("Error closing WebSocket before reconnect:", e);
        }
        wsRef.current = null;
      }

      connectWebSocket();
    }, delay);
  };

  // Add manual retry function
  const handleRetryConnection = () => {
    console.log("🔄 Manual retry requested");
    setError(null);
    reconnectAttemptsRef.current = 0;
    reconnectDelayRef.current = 1000;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (e) {
        console.log("Error closing WebSocket before retry:", e);
      }
      wsRef.current = null;
    }
    connectWebSocket();
  };

  const connectWebSocket = async () => {
    try {
      // Don't create a new connection if one already exists and is connecting or open
      if (wsRef.current) {
        const state = wsRef.current.readyState;
        if (state === WebSocket.CONNECTING || state === WebSocket.OPEN) {
          devLog("Voice WebSocket already exists and is connecting/open, skipping new connection");
          return;
        }
      }

      // Clear any existing reconnect timer
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      const wsUrl = await getWebSocketUrl();
      devLog("Creating new voice WebSocket connection", {
        hasUrl: Boolean(wsUrl),
        apiUrlSet: Boolean(VOICE_API_URL),
        wsUrlSet: Boolean(VOICE_WS_URL),
      });

      const protocols = getWebSocketProtocols();
      const ws = protocols ? new WebSocket(wsUrl, protocols) : new WebSocket(wsUrl);

      ws.onopen = () => {
        devLog("Voice WebSocket connected successfully");
        setIsConnected(true);
        setError(null);
        // Reset reconnect attempts and delay on successful connection
        reconnectAttemptsRef.current = 0;
        reconnectDelayRef.current = 1000;
      };

      // Applies one transcript segment from the server. The backend now forwards BOTH
      // partial (interim, low-latency) and final results — see live_audio_transcribe.py.
      // A partial REPLACES the in-progress guess (AWS revises it in place as more audio
      // arrives); a final PERMANENTLY commits it and clears the interim slot. This makes
      // the visible text update as the user speaks instead of waiting for AWS to decide
      // the utterance is finished, which was the main source of perceived "slow mic" lag.
      const applyTranscriptSegment = (segType: "partial" | "final", rawText: string) => {
        if (ignoreTranscriptRef.current) return;
        const text = rawText.trim();
        if (!text) return;

        if (segType === "final") {
          const prevCommitted = committedVoiceTextRef.current;
          if (!prevCommitted) {
            committedVoiceTextRef.current = text;
          } else {
            const prevTrimmed = prevCommitted.trim();
            if (text.startsWith(prevTrimmed) && text.length > prevTrimmed.length) {
              committedVoiceTextRef.current = text;
            } else if (text !== prevTrimmed) {
              const needsSpace = !prevCommitted.endsWith(" ");
              committedVoiceTextRef.current = needsSpace ? `${prevCommitted} ${text}` : `${prevCommitted}${text}`;
            }
          }
          // The finalized segment supersedes whatever partial guess led up to it.
          interimVoiceTextRef.current = "";
        } else {
          interimVoiceTextRef.current = text;
        }

        let combined = [sessionPrefixRef.current, committedVoiceTextRef.current, interimVoiceTextRef.current]
          .filter((s) => s && s.length > 0)
          .join(" ");
        if (combined.length > MAX_TEXT_LENGTH) {
          devLog("Text length exceeded maximum, truncating to prevent memory issues");
          combined = combined.substring(0, MAX_TEXT_LENGTH);
        }

        // Update ref immediately (single source of truth) - CRITICAL: Always do this first
        accumulatedTextRef.current = combined;

        devLog(`Voice ${segType} text applied`, { length: combined.length });

        // Set flag to prevent useEffect interference
        isVoiceUpdateRef.current = true;

        // Update DOM directly FIRST for instant visual feedback (bypasses React)
        try {
          if (inputRef.current) {
            inputRef.current.value = combined;
            if (inputRef.current.setSelectionRange) {
              const length = combined.length;
              inputRef.current.setSelectionRange(length, length);
              inputRef.current.scrollLeft = inputRef.current.scrollWidth;
            }
          }
        } catch (error) {
          devError("Error updating DOM", error);
        }

        // Smart batching: Update React state with throttling to prevent overload
        const now = Date.now();
        const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

        if (timeSinceLastUpdate >= MAX_UPDATE_RATE || !pendingUpdateRef.current) {
          lastUpdateTimeRef.current = now;
          pendingUpdateRef.current = true;
          try {
            setDisplayValue(accumulatedTextRef.current);
            field.onChange({
              target: { name: field.name, value: accumulatedTextRef.current },
            });
            pendingUpdateRef.current = false;
            updateQueueRef.current = null;
          } catch (error) {
            devError("Error updating React state", error);
            pendingUpdateRef.current = false;
          }
        } else {
          updateQueueRef.current = accumulatedTextRef.current;
          const timeoutId = setTimeout(() => {
            const latestText = accumulatedTextRef.current;
            if (updateQueueRef.current !== null) {
              try {
                setDisplayValue(latestText);
                field.onChange({ target: { name: field.name, value: latestText } });
                updateQueueRef.current = null;
                pendingUpdateRef.current = false;
                lastUpdateTimeRef.current = Date.now();
              } catch (error) {
                devError("Error in batched update", error);
                pendingUpdateRef.current = false;
                updateQueueRef.current = null;
              }
            }
          }, MAX_UPDATE_RATE - timeSinceLastUpdate);
          (timeoutId as any).__voiceUpdate = true;
        }

        setTimeout(() => {
          isVoiceUpdateRef.current = false;
        }, 10);
      };

      ws.onmessage = (event) => {
        if (typeof event.data !== 'string') return;
        const raw = event.data.trim();
        if (!raw) return;

        if (!raw.startsWith('{')) {
          // Back-compat: an older/other backend sending plain-text transcript treats it
          // as a final segment.
          devLog("Voice text received (plain)", { length: raw.length });
          applyTranscriptSegment("final", raw);
          return;
        }

        try {
          const data = JSON.parse(raw);
          if (!data || typeof data !== 'object') return;

          // Transcript segment envelope from live_audio_transcribe.py: {type, text}.
          if ((data.type === 'partial' || data.type === 'final') && typeof data.text === 'string') {
            applyTranscriptSegment(data.type, data.text);
            return;
          }

          // Server-side error (no-audio timeout, missing AWS credentials, etc.) —
          // surface as a banner, never as dictated text in the chat input. Only
          // shown if the user has actually pressed the mic on this connection —
          // the connection itself stays open in the background whether or not
          // voice is ever used, so the backend's no-first-audio timeout would
          // otherwise fire for someone who never touched the mic at all.
          if (data.type === 'error' && typeof data.message === 'string') {
            if (recordingAttemptedRef.current) {
              setError(data.message);
            } else {
              devLog("Ignoring voice WS error — mic was never engaged on this connection", {
                message: data.message,
              });
            }
            return;
          }

          // Processing response (LLM turn result), unrelated to live transcription.
          if (data.type === 'process_response' || data.data) {
            devLog("Received processing response via WebSocket", { hasData: Boolean(data) });
            if (onProcessResponse) {
              onProcessResponse(data);
            }
            return;
          }
        } catch (error) {
          devError("Error processing WebSocket message", error);
        }
      };

      ws.onerror = (error) => {
        devError("Voice WebSocket error", error);
        devLog("Voice WebSocket readyState", { readyState: ws?.readyState });
        setError("Connection error. Retrying...");
        setIsConnected(false);
        // Don't attempt reconnect here - let onclose handle it
      };

      ws.onclose = (event) => {
        devLog("Voice WebSocket disconnected", {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          hasUrl: Boolean(wsUrl)
        });
        setIsConnected(false);

        if (isRecording) {
          stopRecording();
        }

        // 1008 (policy violation) is how the orchestration rejects an expired/invalid
        // session on WS connect (see _authenticate_websocket_on_connect / the matching
        // check in websocketManager.ts). Retrying with a dead token just loops
        // "Connection error. Retrying…" forever — log the user out to the login screen
        // instead of attempting a normal reconnect.
        if (event.code === 1008) {
          isManualCloseRef.current = true; // stop any scheduled reconnect
          handleExpiredAuthSession();
          return;
        }

        // Only attempt reconnection if not manually closed and not unmounting
        if (!isManualCloseRef.current && !event.wasClean) {
          attemptReconnect();
        }
      };

      wsRef.current = ws;
    } catch (err: any) {
      devError("Failed to connect voice WebSocket", err);
      setError(`Connection failed: ${err.message}. Retrying...`);
      setIsConnected(false);

      // Attempt reconnection on error
      if (!isManualCloseRef.current) {
        attemptReconnect();
      }
    }
  };

  // Function to send processing request via WebSocket
  const sendProcessingRequest = (text: string, metadata?: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const payload = createProcessingPayload(text, metadata);
      try {
        devLog("Sending processing request via WebSocket", { hasPayload: Boolean(payload) });
        wsRef.current.send(JSON.stringify(payload));
      } catch (err) {
        devError("Error sending processing request", err);
        setError("Failed to send request. Attempting to reconnect...");
        // Try to reconnect and retry
        if (!isManualCloseRef.current) {
          attemptReconnect();
        }
      }
    } else {
      devError("WebSocket is not connected. Cannot send processing request.");
      setError("Connection lost. Attempting to reconnect...");

      // Try to reconnect
      if (!isManualCloseRef.current) {
        connectWebSocket();

        // Wait for connection and retry (with timeout)
        const retryTimeout = setTimeout(() => {
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            const payload = createProcessingPayload(text, metadata);
            try {
              wsRef.current.send(JSON.stringify(payload));
              setError(null); // Clear error on success
            } catch (err) {
              devError("Error sending processing request after reconnect", err);
              setError("Failed to send request after reconnection.");
            }
          } else {
            setError("Reconnection failed. Please try again.");
          }
        }, 2000); // Wait up to 2 seconds for reconnection

        // Cleanup timeout if component unmounts
        return () => clearTimeout(retryTimeout);
      }
    }
  };

  // Expose sendProcessingRequest via callback when WebSocket is ready
  useEffect(() => {
    if (onProcessRequestReady && isConnected && wsRef.current?.readyState === WebSocket.OPEN) {
      onProcessRequestReady(sendProcessingRequest);
    }
  }, [isConnected, onProcessRequestReady]);

  // Flush pending updates immediately
  // CRITICAL: Always use ref value to ensure no text is lost
  const flushPendingUpdates = () => {
    // Clear any pending queued updates
    updateQueueRef.current = null;
    pendingUpdateRef.current = false;

    // Immediately update state with current accumulated text from ref
    // CRITICAL: Always use ref (source of truth) to ensure nothing is lost
    try {
      const currentText = accumulatedTextRef.current;

      // Update DOM first to ensure visual consistency
      if (inputRef.current) {
        inputRef.current.value = currentText;
      }

      // Then update React state
      setDisplayValue(currentText);
      field.onChange({
        target: {
          name: field.name,
          value: currentText,
        },
      });
      isVoiceUpdateRef.current = false;
      lastUpdateTimeRef.current = Date.now();
    } catch (error) {
      console.error("Error flushing pending updates:", error);
      // Don't throw - continue, but log the error
      // Text is still in ref, so it's not lost
    }
  };

  const stopRecording = () => {
    setIsRecording(false);
    resetTransientRippleTokens();
    stopWaveVisualizer();

    // Tell the backend to finalize and close THIS recording's AWS Transcribe
    // stream (see live_audio_transcribe.py's stop_session). Sent first, before
    // local teardown, so AWS starts finalizing in parallel with the local audio
    // graph cleanup below rather than after it.
    if (
      recordingSessionActiveRef.current &&
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN
    ) {
      try {
        wsRef.current.send(JSON.stringify({ type: "stop_recording" }));
      } catch (e) {
        console.log("Error sending stop_recording:", e);
      }
    }
    recordingSessionActiveRef.current = false;

    // Flush any pending debounced updates to ensure final text is captured
    flushPendingUpdates();

    if (silenceWatchdogRef.current) {
      clearTimeout(silenceWatchdogRef.current);
      silenceWatchdogRef.current = null;
    }

    if (audioTrackRef.current) {
      audioTrackRef.current.onmute = null;
      audioTrackRef.current.onunmute = null;
      audioTrackRef.current.onended = null;
      audioTrackRef.current = null;
    }

    // Tell the worklet to stop processing
    if (workletNodeRef.current) {
      try {
        workletNodeRef.current.port.postMessage({ command: "stop" });
        workletNodeRef.current.disconnect();
      } catch (e) {
        console.log("Error disconnecting worklet:", e);
      }
      workletNodeRef.current = null;
    }

    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch (e) {
        console.log("Error disconnecting source:", e);
      }
      sourceRef.current = null;
    }

    // Suspend AudioContext instead of closing to allow reuse
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.suspend().catch((e) => {
        console.log("Error suspending audio context:", e);
        // If suspend fails, close it
        try {
          audioContextRef.current?.close();
        } catch (closeErr) {
          console.log("Error closing audio context:", closeErr);
        }
        audioContextRef.current = null;
      });
    }

    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Don't clean up blob URL here - we'll reuse it for next recording
    // Only clean up on component unmount
  };

  // Leaving Agentic mode must stop any in-progress mic session.
  useEffect(() => {
    if (!isAgenticMode && isRecording) {
      stopRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgenticMode]);

  const startRecording = async () => {
    try {
      setError(null);
      recordingAttemptedRef.current = true;

      // Ensure WebSocket is connected - auto-reconnect if disconnected
      if (
        !isConnected ||
        !wsRef.current ||
        wsRef.current.readyState !== WebSocket.OPEN
      ) {
        console.log("WebSocket not connected, attempting to reconnect...");

        // Close existing WebSocket if it exists and is not already closed
        if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
          try {
            wsRef.current.close();
          } catch (e) {
            console.log("Error closing existing WebSocket:", e);
          }
          wsRef.current = null;
        }

        // Connect WebSocket
        connectWebSocket();

        // Wait for connection to establish (with retry logic)
        let retries = 0;
        const maxRetries = 10; // 5 seconds total (10 * 500ms)

        while (retries < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Check WebSocket readyState directly (more reliable than isConnected state)
          if (
            wsRef.current &&
            wsRef.current.readyState === WebSocket.OPEN
          ) {
            console.log("WebSocket reconnected successfully");
            setError(null); // Clear error when connection succeeds
            // Reset reconnect attempts on successful connection
            reconnectAttemptsRef.current = 0;
            reconnectDelayRef.current = 1000;
            break;
          }
          retries++;
        }

        // Final check after retries
        if (
          !wsRef.current ||
          wsRef.current.readyState !== WebSocket.OPEN
        ) {
          setError("Failed to establish WebSocket connection. Please try again.");
          // Trigger automatic reconnection
          if (!isManualCloseRef.current) {
            attemptReconnect();
          }
          return;
        } else {
          // Connection succeeded, clear any previous error
          setError(null);
        }
      }

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1, // Mono
          sampleRate: 16000, // 16kHz as required by AWS Transcribe
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      streamRef.current = stream;

      // Tell the backend to open THIS recording's own AWS Transcribe stream (see
      // live_audio_transcribe.py's start_session) — sent as soon as mic access is
      // confirmed so AWS's stream setup round-trip overlaps with the (prewarmed,
      // now near-instant) local audio graph wiring below instead of adding to it.
      // Each start/stop pair gets an independently-finalized transcript instead of
      // sharing one continuous stream for the connection's whole lifetime, which
      // previously let a stale, not-yet-finalized result from a quick prior
      // recording bleed into the next one.
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({ type: "start_recording" }));
          recordingSessionActiveRef.current = true;
        } catch (e) {
          console.log("Error sending start_recording:", e);
        }
      }

      // Detect the case where the OS/browser has granted the stream but the
      // track produces no audio — e.g. Google Meet (or another conferencing
      // app) holding exclusive control of the mic. In that state getUserMedia
      // still resolves and recording "starts", but the track is muted so no
      // real audio ever reaches the worklet/transcript.
      const audioTrack = stream.getAudioTracks()[0] || null;
      audioTrackRef.current = audioTrack;
      if (audioTrack) {
        if (audioTrack.muted) {
          setError(
            "Microphone is muted or in use by another app (e.g. Google Meet). Close/mute the other app and retry."
          );
        }
        audioTrack.onmute = () => {
          setError(
            "Microphone was taken over by another app (e.g. Google Meet). Recording will not capture audio until it's released."
          );
        };
        audioTrack.onunmute = () => {
          setError(null);
        };
        audioTrack.onended = () => {
          setError("Microphone connection was lost. Please start recording again.");
          stopRecording();
        };
      }

      // Reuses the context + worklet module prewarmed on mount (falls back to loading
      // them now if the prewarm hasn't finished or failed).
      const audioContext = await ensureAudioPipelineReady();
      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      // Create audio source from stream
      const source = audioContext.createMediaStreamSource(stream);
      sourceRef.current = source;

      // Create AudioWorkletNode for PCM conversion
      const workletNode = new AudioWorkletNode(
        audioContext,
        "audio-capture-processor"
      );
      workletNodeRef.current = workletNode;

      // Handle PCM data from the worklet
      workletNode.port.onmessage = (event) => {
        pcmFramesReceivedRef.current += 1;

        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          return;
        }

        // Send PCM audio data to WebSocket
        try {
          wsRef.current.send(event.data.pcmData);
        } catch (err) {
          console.error("Error sending audio data:", err);
        }
      };

      // Connect source -> worklet -> destination
      source.connect(workletNode);
      workletNode.connect(audioContext.destination);

      // Parallel, read-only tap for the soundwave visual — does not affect
      // the worklet/WebSocket path above.
      startWaveVisualizer(audioContext, source);

      setIsRecording(true);
      resetTransientRippleTokens();

      // Watchdog: if no PCM frames have arrived a few seconds after starting,
      // the mic is producing no audio (commonly because another app such as
      // Google Meet holds it). Surface this instead of silently "recording"
      // with nothing to transcribe.
      pcmFramesReceivedRef.current = 0;
      if (silenceWatchdogRef.current) {
        clearTimeout(silenceWatchdogRef.current);
      }
      silenceWatchdogRef.current = setTimeout(() => {
        if (pcmFramesReceivedRef.current === 0) {
          setError(
            "No audio detected from the microphone. It may be in use by another app (e.g. Google Meet) — close/mute it and retry."
          );
        }
        silenceWatchdogRef.current = null;
      }, 4000);

      // Capture whatever is already typed/transcribed as this session's prefix so new
      // voice text is APPENDED to it, not a replacement. (Previously this cleared the
      // field on every recording start, wiping out existing typed/voice text.)
      ignoreTranscriptRef.current = false;
      setTranscript("");
      sessionPrefixRef.current = (accumulatedTextRef.current || displayValue || field.value || "").trimEnd();
      committedVoiceTextRef.current = "";
      interimVoiceTextRef.current = "";
    } catch (err: any) {
      console.error("Error starting recording:", err);
      setError(`Failed to start recording: ${err.message}`);
      setIsRecording(false);
      // If start_recording was already sent (e.g. getUserMedia succeeded but the
      // AudioWorklet setup below it failed), tell the backend to close that AWS
      // stream rather than leaving it open with nothing left to feed it.
      if (
        recordingSessionActiveRef.current &&
        wsRef.current &&
        wsRef.current.readyState === WebSocket.OPEN
      ) {
        try {
          wsRef.current.send(JSON.stringify({ type: "stop_recording" }));
        } catch (sendErr) {
          console.log("Error sending stop_recording after failed start:", sendErr);
        }
      }
      recordingSessionActiveRef.current = false;
      if (err.name === "NotAllowedError") {
        setError(
          "Microphone access denied. Please allow microphone access and try again."
        );
      } else if (err.name === "NotFoundError") {
        setError(
          "No microphone found. Please connect a microphone and try again."
        );
      }
    }
  };

  // Handle key down events - stop recording on Enter
  const handleKeyDown = (e: any) => {
    // If Enter key is pressed (without Shift for multiline)
    if (e.key === "Enter" && !e.shiftKey) {
      // Stop recording if active
      if (isRecording) {
        console.log("Enter pressed - stopping recording");
        stopRecording();

        // Trigger voice result callback
        if (onVoiceResult && accumulatedTextRef.current) {
          onVoiceResult(accumulatedTextRef.current);
        }
      }

      // Clear input after Enter key (regardless of recording state), same as the
      // Send-button handler. This MUST be the same full reset as clearSearchInput() —
      // previously this only zeroed displayValue/field.value/accumulatedTextRef and left
      // sessionPrefixRef/committedVoiceTextRef/interimVoiceTextRef stale. The next voice
      // recording session's prefix capture (see startRecording) reads those refs as its
      // starting point, so a stale committedVoiceTextRef from THIS turn silently prepended
      // itself to every subsequent turn's transcript — this turn's whole sentence kept
      // reappearing as a growing prefix on each later voice message.
      clearSearchInput();
    }

    // Call parent's onKeyDown handler
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  // Custom onChange handler
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;

    // Update ref first (single source of truth)
    accumulatedTextRef.current = value;

    // Update local state immediately
    setDisplayValue(value);

    // Update Formik with raw typed value to preserve caret position.
    // Input sanitization is handled at submit/search time.
    field.onChange({
      target: {
        name: field.name,
        value,
      },
    });

    // CRITICAL FIX: Forward onChange to parent (SearchInputSection) so it can
    // persist the draft to the module-level store via setDraftInputText.
    // VoiceSearchInput was consuming the onChange event without forwarding it.
    if (propsOnChange) {
      propsOnChange(event);
    }
  };

  const cleanField = {
    name: field.name,
    value: field.value,
    onChange: handleChange,
    onBlur: field.onBlur,
  };

  const handleVttFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!isVttFileName(file.name)) {
      setError("Please upload a .vtt file");
      return;
    }
    if (file.size > MAX_VTT_FILE_BYTES) {
      setError(`VTT file must be under ${MAX_VTT_FILE_BYTES / (1024 * 1024)} MB`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      if (!isValidWebVttHeader(text)) {
        setError("File must be valid WebVTT (start with WEBVTT)");
        return;
      }
      setError(null);
      if (onSearch) {
        if (isRecording) {
          stopRecording();
        }
        const typedText = (
          accumulatedTextRef.current ||
          displayValue ||
          field.value ||
          ""
        ).trimEnd();
        onSearch(typedText, { vttContent: text });
        onVttContentChange?.(null);
        if (vttFileInputRef.current) {
          vttFileInputRef.current.value = "";
        }
        setDisplayValue("");
        field.onChange({
          target: {
            name: field.name,
            value: "",
          },
        });
        accumulatedTextRef.current = "";
      } else {
        onVttContentChange?.(text);
      }
    };
    reader.onerror = () => setError("Could not read file");
    reader.readAsText(file);
  };

  const handleVoiceSearch = () => {
    if (isRecording) {
      // Stop only — do not auto-search or clear. User can edit, then send via arrow.
      stopRecording();
      return;
    }
    startRecording();
  };

  /** Clears typed/voice text and session transcript state (same as Clear X). */
  const clearSearchInput = () => {
    ignoreTranscriptRef.current = true;
    updateQueueRef.current = null;
    pendingUpdateRef.current = false;
    isVoiceUpdateRef.current = false;
    accumulatedTextRef.current = "";
    sessionPrefixRef.current = "";
    committedVoiceTextRef.current = "";
    interimVoiceTextRef.current = "";
    setTranscript("");
    setDisplayValue("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    field.onChange({
      target: { name: field.name, value: "" },
    });
    onVttContentChange?.(null);
    if (vttFileInputRef.current) {
      vttFileInputRef.current.value = "";
    }
  };

  const handleSearch = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    // Flush any pending voice text before reading, then stop if still recording.
    flushPendingUpdates();
    const text = (accumulatedTextRef.current || displayValue || "").trimEnd();
    const vttTrim = pendingVttContent?.trim();
    const vttPayload = vttTrim ? pendingVttContent! : undefined;

    if (isRecording) {
      stopRecording();
    }

    // Always clear the input on arrow/send.
    // Do this before onSearch so late WS finals cannot race the clear.
    clearSearchInput();

    if (!text.trim() && !vttPayload) {
      return;
    }

    if (onSearch) {
      onSearch(text, vttPayload ? { vttContent: vttPayload } : undefined);
    }
  };


  return (
    <VoiceSearchInputContainer>
      {/* Transient status badges float above the pill so they never alter its layout */}
      {error && (
        <Box
          sx={{
            position: "absolute",
            top: "-34px",
            right: "8px",
            backgroundColor: "error.main",
            color: "white",
            padding: "4px 12px",
            borderRadius: "12px",
            fontSize: "0.8rem",
            fontWeight: 600,
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <span>{error}</span>
          {error.includes("Connection lost") && (
            <IconButton
              size="small"
              onClick={handleRetryConnection}
              sx={{
                color: "white",
                padding: "2px",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            aria-label="Dismiss"
            onClick={() => setError(null)}
            sx={{
              color: "white",
              padding: "2px",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      {typeof voiceSearchCountdown === "number" && voiceSearchCountdown > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: "-34px",
            right: "8px",
            backgroundColor: primaryColor,
            color: "white",
            padding: "4px 12px",
            borderRadius: "12px",
            fontSize: "0.8rem",
            fontWeight: 600,
            zIndex: 1000,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          Auto-searching in {voiceSearchCountdown}s...
        </Box>
      )}

      {/* Hidden .vtt picker driven by the upload button below */}
      <input
        type="file"
        accept=".vtt,text/vtt"
        ref={vttFileInputRef}
        style={{ display: "none" }}
        onChange={handleVttFileChange}
      />

      {/* Flat pill: mode dropdown (left) + text + mic/upload/send (right) */}
      <VoiceSearchPill>
        {/* Mode dropdown: Voice / Transcript with icons, matching the pill. */}
        {onPrescribeModeChange && (
          <>
            <Box
              component="button"
              type="button"
              disabled={isDisabled}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                setModeMenuAnchor(e.currentTarget);
              }}
              aria-haspopup="listbox"
              aria-expanded={modeMenuOpen}
              aria-label={`Prescribe mode: ${modeLabel}`}
              sx={{
                all: "unset",
                boxSizing: "border-box",
                cursor: isDisabled ? "default" : "pointer",
                flex: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                height: 28,
                pl: 0.5,
                pr: 0.5,
                mr: 0.25,
                borderRadius: "16px",
                color: primaryColor,
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: "nowrap",
                lineHeight: 1,
                opacity: isDisabled ? 0.5 : 1,
                backgroundColor: modeMenuOpen ? `${primaryColor}14` : "transparent",
                transition: "background-color 0.15s ease",
                "&:hover": isDisabled ? undefined : { backgroundColor: `${primaryColor}14` },
                "&:focus-visible": {
                  outline: `2px solid ${primaryColor}`,
                  outlineOffset: 2,
                },
              }}
            >
              <ModeTriggerIcon sx={{ fontSize: 16 }} />
              {modeLabel}
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 18,
                  opacity: 0.9,
                  transform: modeMenuOpen ? "rotate(180deg)" : "none",
                  transition: "transform 0.15s ease",
                }}
              />
            </Box>
            <Menu
              anchorEl={modeMenuAnchor}
              open={modeMenuOpen}
              onClose={() => setModeMenuAnchor(null)}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "bottom", horizontal: "left" }}
              MenuListProps={{ dense: true, "aria-label": "Prescribe mode" }}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    borderRadius: "14px",
                    minWidth: 196,
                    mt: -0.75,
                    py: 0.5,
                    px: 0.5,
                    boxShadow: `0 8px 28px ${primaryColor}22`,
                    border: `1px solid ${primaryColor}22`,
                    overflow: "hidden",
                    "& .MuiList-root": { py: 0 },
                  },
                },
              }}
            >
              {PRESCRIBE_MODE_OPTIONS.map((opt) => {
                const selected = prescribeMode === opt.id;
                const OptionIcon = opt.Icon;
                return (
                  <MenuItem
                    key={opt.id}
                    selected={selected}
                    onClick={() => {
                      onPrescribeModeChange(opt.id);
                      setModeMenuAnchor(null);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mx: 0,
                      my: 0.25,
                      px: 1,
                      py: 0.85,
                      borderRadius: "10px",
                      minHeight: 0,
                      fontSize: 13,
                      fontWeight: selected ? 600 : 500,
                      color: selected ? primaryColor : "text.primary",
                      "&.Mui-selected": {
                        backgroundColor: `${primaryColor}14`,
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: `${primaryColor}22`,
                      },
                      "&:hover": {
                        backgroundColor: selected ? `${primaryColor}22` : `${primaryColor}0a`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        flex: "none",
                        backgroundColor: selected ? `${primaryColor}22` : `${primaryColor}12`,
                        color: primaryColor,
                      }}
                    >
                      <OptionIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                      <Box component="span" sx={{ lineHeight: 1.2 }}>
                        {opt.label}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontSize: 11,
                          fontWeight: 400,
                          color: selected ? `${primaryColor}bb` : "text.secondary",
                          lineHeight: 1.3,
                          mt: 0.15,
                        }}
                      >
                        {opt.hint}
                      </Box>
                    </Box>
                    {selected ? (
                      <CheckRoundedIcon sx={{ fontSize: 16, color: primaryColor, flex: "none" }} />
                    ) : (
                      <Box sx={{ width: 16, flex: "none" }} />
                    )}
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        )}

        {/* Text field and its soundwave overlay share this slot — the overlay is
            absolutely positioned within it, so swapping between them never
            changes the pill's layout/width. */}
        <Box
          ref={waveSlotRef}
          sx={{ position: "relative", flex: 1, display: "flex", alignItems: "center", minWidth: 0 }}
        >
          <TextField
            variant="standard"
            type={multiline ? undefined : "text"}
            multiline={multiline}
            minRows={multiline ? rows || 1 : undefined}
            maxRows={multiline ? maxRows || 6 : undefined}
            error={Boolean(meta.touched && meta.error)}
            // Typing must stay available while a previous turn is in flight — only the
            // submit affordances (Send button below, Enter key in the parent's
            // handleKeyDown) are gated by `disabled`. The actual duplicate-submission
            // guard lives centrally in AIPrescription/index.tsx's handleSearch, so this
            // is a UX affordance, not the enforcement point.
            name={cleanField.name}
            value={displayValue}
            onChange={handleChange}
            onBlur={cleanField.onBlur}
            onKeyDown={handleKeyDown}
            id={id}
            label={undefined}
            placeholder={placeholder}
            className={`voice-search-input ${className || ""}`}
            autoComplete="off"
            spellCheck={false}
            sx={{
              flex: 1,
              opacity: isRecording ? 0 : 1,
              transition: "opacity 0.25s ease",
              pointerEvents: isRecording ? "none" : "auto",
              "& .MuiInputBase-root": {
                padding: "4px 6px",
                backgroundColor: "transparent",
                fontSize: "15px",
              },
              "& .MuiInputBase-input": {
                maxHeight: multiline ? "160px" : "auto",
                overflowY: multiline ? "auto" : "visible",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#9aa5ad",
                opacity: 1,
              },
            }}
            InputProps={{
              disableUnderline: true,
              inputRef: (el: HTMLInputElement | HTMLTextAreaElement | null) => {
                inputRef.current = el;
              },
            }}
          />

          {/* Soundwave overlay — reacts to the real mic input via waveAnalyserRef,
              only visible while actually recording. */}
          <VoiceWaveContainer className={isRecording ? "active" : undefined}>
            {Array.from({ length: waveBarCount }).map((_, index) => (
              <VoiceWaveBar
                key={index}
                ref={(el: HTMLDivElement | null) => {
                  waveBarRefs.current[index] = el;
                }}
                sx={{ backgroundColor: getWaveBarColor(index, waveBarCount) }}
              />
            ))}
          </VoiceWaveContainer>
        </Box>
         <Box
          sx={{
            flex: "none",
            width: 44,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {/* Agentic Rx — mic / stop */}
          {isAgenticMode && (
            <MicButtonWrapper>
              {isRecording && <RecordingIndicator />}
              <Tooltip title={isRecording ? "Stop recording" : "Start voice search"}>
                <IconButton
                  onClick={() => {
                    triggerRipple("mic");
                    if (isRecording) {
                      setTimeout(() => handleVoiceSearch(), 50);
                    } else {
                      handleVoiceSearch();
                    }
                  }}
                  aria-label={isRecording ? "Stop recording" : "Start voice test search"}
                  disableRipple
                  sx={{
                    flex: "none",
                    width: 40,
                    height: 40,
                    padding: 0,
                    color: isRecording ? "error.main" : primaryColor,
                    transition: "color 0.2s ease, background-color 0.2s ease",
                    "&:hover": { backgroundColor: `${primaryColor}26` },
                    ...rippleContainerSx,
                  }}
                >
                  {rippleTokens.mic ? (
                    <RippleSpan
                      key={rippleTokens.mic}
                      sx={{ backgroundColor: isRecording ? "#ff4444" : primaryColor }}
                    />
                  ) : null}
                  {isRecording ? (
                    <StopCircleOutlinedIcon sx={{ fontSize: 26 }} />
                  ) : (
                    <MicNoneIcon sx={{ fontSize: 26 }} />
                  )}
                </IconButton>
              </Tooltip>
            </MicButtonWrapper>
          )}

          {/* Transcribe Rx — plain button (no MUI IconButton ripple, which
              escaped into the whole pill because the pill forces position:static). */}
          {isTranscribeMode && (
            <Tooltip title="Upload WebVTT (.vtt)">
              <Box
                component="button"
                type="button"
                onClick={() => {
                  vttFileInputRef.current?.click();
                }}
                aria-label="Upload WebVTT file"
                sx={{
                  all: "unset",
                  boxSizing: "border-box",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: `${primaryColor}1a`,
                  color: primaryColor,
                  transition: "background-color 0.15s ease",
                  "&:hover": { backgroundColor: `${primaryColor}2e` },
                  "&:focus-visible": {
                    outline: `2px solid ${primaryColor}`,
                    outlineOffset: 1,
                  },
                }}
              >
                <UploadFileIcon sx={{ fontSize: 20 }} />
              </Box>
            </Tooltip>
          )}
        </Box>

    

        {/* While recording (Agentic): submit dictated text (right side). */}
        {isAgenticMode && isRecording && showSendArrow && (
          <IconButtonWrapper>
            <Tooltip title={isDisabled ? "Processing..." : "Send"}>
              <span>
                <IconButton
                  onClick={(e) => {
                    if (isDisabled) return;
                    triggerRipple("done");
                    setTimeout(() => handleSearch(e), 50);
                  }}
                  disabled={isDisabled}
                  aria-label={isDisabled ? "Processing" : "Send"}
                  disableRipple
                  sx={{
                    flex: "none",
                    width: 34,
                    height: 34,
                    padding: 0,
                    color: primaryColor,
                    transition: "all 0.2s ease",
                    "&:hover": { backgroundColor: `${primaryColor}26` },
                    ...rippleContainerSx,
                    "&.Mui-disabled": { color: `${primaryColor}55` },
                  }}
                >
                  {rippleTokens.done ? <RippleSpan key={rippleTokens.done} sx={{ backgroundColor: primaryColor }} /> : null}
                  {isDisabled ? (
                    <CircularProgress size={20} thickness={4} sx={{ color: primaryColor }} />
                  ) : (
                    <SendIcon sx={{ fontSize: 24 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </IconButtonWrapper>
        )}

        {/* Main Send button — hidden while recording or when the input is empty */}
        {!isRecording && showSendArrow && (
          <IconButtonWrapper>
            <Tooltip title={isDisabled ? "Processing..." : "Send"}>
              <span>
                <IconButton
                  onClick={(e) => {
                    if (isDisabled) return;
                    triggerRipple("send");
                    handleSearch(e);
                  }}
                  disabled={isDisabled}
                  aria-label={isDisabled ? "Processing" : "Send"}
                  disableRipple
                  sx={{
                    flex: "none",
                    width: 34,
                    height: 34,
                    padding: 0,
                    color: primaryColor,
                    transition: "all 0.2s ease",
                    "&:hover": { backgroundColor: `${primaryColor}26` },
                    ...rippleContainerSx,
                    "&.Mui-disabled": { color: `${primaryColor}55` },
                  }}
                >
                  {rippleTokens.send ? <RippleSpan key={rippleTokens.send} sx={{ backgroundColor: primaryColor }} /> : null}
                  {isDisabled ? (
                    <CircularProgress size={20} thickness={4} sx={{ color: primaryColor }} />
                  ) : (
                    <SendIcon sx={{ fontSize: 26 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </IconButtonWrapper>
        )}

        {/* Green blinking connection indicator */}
        {isConnected && <ConnectionIndicator />}
      </VoiceSearchPill>

      {meta.error && (
        <Typography
          color="error"
          className="error"
          variant="body2"
          component="div"
        >
          {meta.error}
        </Typography>
      )}
    </VoiceSearchInputContainer>
  );
}
